import { useState } from "react";
import { Link } from "react-router-dom";

interface AccordionSectionItem {
  question: string;
  answer: string;
  linkPath: string;
  linkLabel?: string;
}

interface AccordionSectionProps {
  icon: string;
  title: string;
  description?: string;
  items: AccordionSectionItem[];
}

const iconMap: Record<string, string> = {
  subscription: "ri-file-list-line",
  billing: "ri-bank-card-line",
};

export default function AccordionSection({ icon, title, description, items }: AccordionSectionProps) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const iconClass = iconMap[icon] || "ri-question-line";

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-5">
      <h3 className="text-xs font-bold text-gray-400 mb-1 uppercase tracking-widest flex items-center gap-2">
        <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-md">
          <i className={`${iconClass} text-gray-500 text-sm`}></i>
        </div>
        {title}
      </h3>
      {description && (
        <p className="text-gray-400 text-xs mb-2 ml-8">{description}</p>
      )}
      <div className="mt-3">
        {items.map((item, idx) => (
          <div key={idx} className={`${idx < items.length - 1 ? "border-b border-gray-100" : ""}`}>
            <button
              className="flex items-center justify-between w-full px-4 py-3.5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            >
              <span className="text-gray-800 font-medium text-sm">{item.question}</span>
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-2 bg-gray-100 rounded-full">
                <i className={`ri-arrow-down-s-line text-gray-500 transition-transform duration-200 ${openIdx === idx ? "rotate-180" : ""}`}></i>
              </div>
            </button>
            {openIdx === idx && (
              <div className="px-4 pb-4 pt-1">
                <p className="text-gray-500 text-sm leading-relaxed mb-3">{item.answer}</p>
                <Link
                  to={item.linkPath}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  {item.linkLabel || "Read more"}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </div>
                </Link>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
